import { Link, useLocation } from "react-router-dom";
import { Twitter, Linkedin, Instagram, Phone, Mail, MessageCircle } from "lucide-react";

export const Footer = () => {
  const location = useLocation();
  const isIndianRegion = location.pathname.startsWith('/in');
  const phoneNumber = isIndianRegion ? "+91-9910370629" : "+1 647-954-0611";
  const phoneLink = isIndianRegion ? "tel:+919910370629" : "tel:+16479540611";

  return (
    <footer className="bg-gradient-to-br from-blue-50 via-white to-blue-50 text-gray-800 border-t border-blue-100">
      {/* Top Section */}
      <div className="container mx-auto py-16 px-4">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
          {/* How can we help? */}
          <div className="space-y-4">
            <div className="flex items-center gap-2 mb-3">
              <MessageCircle className="w-5 h-5 text-blue-600" />
              <h3 className="text-lg font-semibold text-gray-800">How can we help?</h3>
            </div>
            <Link 
              to="/contact" 
              className="block text-gray-600 hover:text-blue-600 transition-all duration-200 hover:translate-x-1"
            >
              Contact us anytime
            </Link>
            <p className="text-sm text-gray-500">
              We're here to answer your questions and help grow your business.
            </p>
          </div>

          {/* Call us */}
          <div className="space-y-4">
            <div className="flex items-center gap-2 mb-3">
              <Phone className="w-5 h-5 text-green-600" />
              <h3 className="text-lg font-semibold text-gray-800">Call us</h3>
            </div>
            <a 
              href={phoneLink} 
              className="block text-gray-600 hover:text-blue-600 transition-all duration-200 hover:translate-x-1"
            >
              {phoneNumber}
            </a>
            <p className="text-sm text-gray-500">
              Available Monday to Friday, 9 AM - 6 PM
            </p>
          </div>

          {/* Send us a message */}
          <div className="space-y-4">
            <div className="flex items-center gap-2 mb-3">
              <Mail className="w-5 h-5 text-purple-600" />
              <h3 className="text-lg font-semibold text-gray-800">Send us a message</h3>
            </div>
            <a 
              href="mailto:sales@thegrowthby.com" 
              className="block text-gray-600 hover:text-blue-600 transition-all duration-200 hover:translate-x-1"
            >
              sales@thegrowthby.com
            </a>
            <p className="text-sm text-gray-500">
              We'll get back to you within 24 hours
            </p>
          </div>

          {/* Follow us */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-gray-800 mb-3">Follow us</h3>
            <div className="flex space-x-4">
              <a 
                href="#" 
                className="p-2 rounded-full bg-blue-100 hover:bg-blue-200 text-blue-600 transition-all duration-200 hover:scale-110"
                aria-label="Twitter"
              >
                <Twitter size={20} />
              </a>
              <a 
                href="#" 
                className="p-2 rounded-full bg-blue-100 hover:bg-blue-200 text-blue-600 transition-all duration-200 hover:scale-110"
                aria-label="LinkedIn"
              >
                <Linkedin size={20} />
              </a>
              <a 
                href="https://www.instagram.com/thegrowthby" 
                target="_blank" 
                rel="noopener noreferrer" 
                className="p-2 rounded-full bg-blue-100 hover:bg-blue-200 text-blue-600 transition-all duration-200 hover:scale-110"
                aria-label="Instagram"
              >
                <Instagram size={20} />
              </a>
            </div>
            <p className="text-sm text-gray-500">
              Stay updated with our latest insights
            </p>
          </div>
        </div>
      </div>

      {/* Bottom Section */}
      <div className="border-t border-blue-200 bg-blue-100/50">
        <div className="container mx-auto py-8 px-4">
          <div className="flex flex-col md:flex-row justify-between items-center space-y-6 md:space-y-0">
            {/* Logo */}
            <div>
              <Link to="/" className="text-2xl font-bold text-blue-700 hover:text-blue-800 transition-colors">
                TheGrowthBy
              </Link>
              <p className="text-sm text-gray-600 mt-1">Accelerating Business Growth</p>
            </div>

            {/* Navigation */}
            <nav className="bg-white/70 backdrop-blur-sm rounded-xl px-6 py-3 border border-blue-200 shadow-sm">
              <ul className="flex flex-wrap justify-center space-x-6 text-gray-700">
                <li>
                  <Link to="/" className="hover:text-blue-600 transition-colors duration-200 text-sm font-medium">
                    Home
                  </Link>
                </li>
                <li>
                  <Link to="/about" className="hover:text-blue-600 transition-colors duration-200 text-sm font-medium">
                    About us
                  </Link>
                </li>
                <li>
                  <Link to="/services/ppc-marketing" className="hover:text-blue-600 transition-colors duration-200 text-sm font-medium">
                    Services
                  </Link>
                </li>
                <li>
                  <Link to="/blog" className="hover:text-blue-600 transition-colors duration-200 text-sm font-medium">
                    Blog
                  </Link>
                </li>
                <li>
                  <Link to="/contact" className="hover:text-blue-600 transition-colors duration-200 text-sm font-medium">
                    Contact
                  </Link>
                </li>
              </ul>
            </nav>
          </div>

          {/* Copyright */}
          <div className="mt-8 pt-6 border-t border-blue-200 text-center">
            <p className="text-gray-600 text-sm">
              © {new Date().getFullYear()} TheGrowthBy. All rights reserved. | 
              <Link to="/privacy" className="hover:text-blue-600 transition-colors ml-1">Privacy Policy</Link> | 
              <Link to="/terms" className="hover:text-blue-600 transition-colors ml-1">Terms of Service</Link>
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
};